import { ChangeDetectionStrategy, Component, inject, OnInit, signal, WritableSignal } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MODAL_TOKEN, ModalProvider } from '@core';
import { AuthProviderId, AuthService } from '@auth/infra';
import { InputWithIconComponent } from '@ui-components';

export interface RemoveAccountModalResult {
  readonly confirmed: boolean;
  readonly password?: string;
}

@Component({
  selector: 'lib-remove-account-modal',
  templateUrl: './remove-account-modal.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [ReactiveFormsModule, InputWithIconComponent]
})
export class RemoveAccountModalComponent implements OnInit {
  private readonly modalProvider: ModalProvider = inject(MODAL_TOKEN);
  private readonly authService: AuthService = inject(AuthService);

  readonly AuthProviderId: typeof AuthProviderId = AuthProviderId;
  readonly authProviderId: WritableSignal<AuthProviderId | null> = signal<AuthProviderId | null>(null);
  readonly showPassword: WritableSignal<boolean> = signal<boolean>(false);

  readonly passwordForm: FormGroup = new FormGroup({
    password: new FormControl('', [Validators.required])
  });

  ngOnInit(): void {
    this.authService
      .getCurrentProviderId()
      .then((providerId: AuthProviderId) => this.authProviderId.set(providerId))
      .catch(() => this.authProviderId.set(null));
  }

  closeModal(): void {
    this.modalProvider.dismissModal$({ confirmed: false }).subscribe();
  }

  confirm(): void {
    if (this.isConfirmDisabled()) return;

    const provider: AuthProviderId | null = this.authProviderId();
    const result: RemoveAccountModalResult = {
      confirmed: true,
      password: provider === AuthProviderId.Password ? this.passwordForm.get('password')?.value : undefined
    };
    this.modalProvider.dismissModal$(result).subscribe();
  }

  isConfirmDisabled(): boolean {
    const provider: AuthProviderId | null = this.authProviderId();
    if (provider === null) return true;
    if (provider === AuthProviderId.Password) return !this.passwordForm.valid;
    return false;
  }

  toggleShowPassword(): void {
    this.showPassword.update((value: boolean) => !value);
  }
}
